import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Animation from '../../../components/Animation';
import { FetchCallbacksContext } from '../../FetchCallbacks';
import fetchAll from '../../../utils/fetchAll';
import GeneralDialog from '../../GeneralDialog';
import generateId from '../../../utils/generateId';
import useFetchWithCallbacks from '../../../hooks/useFetchWithCallbacks';
import FormBuilderCollaborateView from '../view/FormBuilderCollaborateView';
import FormBuilderCollaborateWizardEngine from './FormBuilderCollaborateWizardEngine';
import AdminGroupWizardEngine from './AdminGroupWizardEngine';
import { sectionPageSizeDefaults } from 'docx';
import useGlobalVariables from '../../../hooks/useGlobalVariables';
import { FormBuilderContext } from '../../../components/engine/FormBuilderEngine';

export const ACTIVE_VIEW = {
  COLLABORATOR_WIZARD: 'cw',
  COLLABORATOR_LIST: 'cl',
  ADMIN_GROUP_WIZARD: 'agw'
};

const getUserLabel = u => {
  let alreadyAdmin = false;
  if (u.rolesData && u.rolesData.role !== 'end_user') {
    alreadyAdmin = true;
  }
  return {
    label: `${u.info.firstName} ${u.info.lastName}`,
    value: u.id,
    _id: generateAdminId(),
    alreadyAdmin: alreadyAdmin,
    firstName: u.info.firstName,
    lastName: u.info.lastName,
    email: u.info.email
  };
};

const generateAdminId = () => 'admin_' + generateId();

const reducer = (state, action) => {
  const { type, admin, groups, notificationInfo, _id, userIds } = action;

  switch (type) {
    case 'load state':
      return action.state;

    case 'add admin':
      return [
        ...state,
        { _id: generateAdminId(), notificationInfo: 'FLAGGED_ONLY', groups: [] }
      ];

    case 'delete admin':
      return state.filter((a, i) => a._id !== _id);

    case 'delete multi admin':
      return state.filter(collaborator => !userIds.includes(collaborator._id));

    case 'set admin':
      var newState = state.map((a, i) =>
        a._id === _id
          ? {
              ...admin,
              notificationInfo: a.notificationInfo,
              groups: a.groups,
              _id
            }
          : a
      );
      return newState;

    case 'set groups':
      return state.map((a, i) => (a._id === _id ? { ...a, groups } : a));

    case 'set notificationInfo':
      return state.map((a, i) =>
        a._id === _id ? { ...a, notificationInfo } : a
      );

    default:
      return state;
  }
};

const notADuplicate = (v, i, a) => a.indexOf(v) === i;

export const CollaboratorsContext = createContext({});

const FormBuilderCollaborateEngine = ({ formId, form, pageId, reloadForm }) => {
  const prevActiveView = useRef(ACTIVE_VIEW.COLLABORATOR_LIST);
  const [activeView, _setActiveView] = useState(ACTIVE_VIEW.COLLABORATOR_LIST);
  const [editMode, setEditMode] = useState(false);
  const [triggerSaveWhenDispatchDone, setTriggerSaveWhenDispatchDone] =
    useState(false);

  const [collaborators, dispatch] = React.useReducer(reducer, []);
  const [eligibleForDefaultAdmin, setEligibleForDefaultAdmin] = useState([]);
  const [collaboratorInEdit, setCollaboratorInEdit] = useState({});
  const serverSets = React.useRef([]);
  const { bucketNames, bucketFields } = useContext(FormBuilderContext);

  const fetchAPI = useFetchWithCallbacks();
  const { openSnackbar } = React.useContext(FetchCallbacksContext);

  const [submitting, setSubmitting] = useState(false);
  const [askingToConfirm, setAskingToConfirm] = useState(false);
  const [filter, setFilter] = useState(null);

  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  const defaultCollaborators = collaborators.filter(c => c.defaultAssignee);
  const [defaultAssignee, setDefaultAssignee] = useState(null);
  const [changedDefaultAssignee, setChangedDefaultAssignee] = useState(false);

  const [allGroups, setAllGroups] = useState([]);

  const [hasSubmissions, setHasSubmissions] = useState(false);

  const { setIsLoading } = useGlobalVariables();

  const existingDefaultAssignee = defaultCollaborators.length
    ? defaultCollaborators[0].value
    : null;

  const collaboratorsWithoutAGroup =
    collaborators.filter(c => !c.group && c.value) || [];

  const setActiveView = useCallback(
    value => {
      prevActiveView.current = activeView;
      _setActiveView(value);
    },
    [activeView]
  );

  const openFormAdminWizard = useCallback(
    collaboratorData => {
      setCollaboratorInEdit(collaboratorData);
      setActiveView(ACTIVE_VIEW.COLLABORATOR_WIZARD);
      setEditMode(JSON.stringify(collaboratorData) !== '{}');
    },
    [setActiveView]
  );

  const openAdminGroupWizard = useCallback(
    collaboratorData => {
      setCollaboratorInEdit(collaboratorData);
      setActiveView(ACTIVE_VIEW.ADMIN_GROUP_WIZARD);
      setEditMode(JSON.stringify(collaboratorData) !== '{}');
    },
    [setActiveView]
  );

  const fetchGroups = useCallback(() => {
    let url = '/users/v1/usergroup';
    if (!(form && form.allowExternalUserAccounts)) {
      url += `?query=[[{"field":"'schemaCode'", "op": "=", "value": "'internal'"}]]`;
    }
    fetchAll(url).then(({ results: r }) => {
      setAllGroups(
        r.map(ug => ({
          text: ug.code,
          label: ug.code,
          value: ug.id
        }))
      );
    });
  }, [
    form,
    form &&
      form.endUserPermissions &&
      form.endUserPermissions.internal &&
      form.endUserPermissions.internal.allowUsersToInviteExternalPeople
  ]);

  useEffect(() => {
    fetchGroups();
  }, [
    fetchGroups,
    form &&
      form.endUserPermissions &&
      form.endUserPermissions.internal &&
      form.endUserPermissions.internal.allowUsersToInviteExternalPeople
  ]);

  useEffect(() => {
    let newCandidates, newCandidateIds;
    newCandidates = [];
    newCandidateIds = [];
    if (collaborators) {
      newCandidates = collaborators.filter(
        one => !one.groups && !one.query && !one.viewOnly
      );
      newCandidateIds = newCandidates.map(one => one.value);
    }
    setEligibleForDefaultAdmin(newCandidates);
    if (defaultAssignee && newCandidateIds.indexOf(defaultAssignee) === -1) {
      setChangedDefaultAssignee(true);
      setDefaultAssignee(null);
    }
  }, [collaborators]);

  const loadCollaborators = () => {
    if (!formId) {
      return;
    }

    fetchAPI(
      '/forms/v1/forms/' +
        formId +
        '/submissions?query=[[{"what":"count","field":"*", "as": "count"}]]'
    ).then(res => {
      if (
        res &&
        res.additional &&
        res.additional.length &&
        res.additional[0].value
      ) {
        setHasSubmissions(true);
      }
    });

    fetchAPI(`/forms/v1/forms/${formId}/collaborate`).then(({ results }) => {
      // store in a ref we can update for later diffing
      serverSets.current = results;

      // no results, nothing left to do
      if (results.length === 0) {
        dispatch({
          type: 'load state',
          state: []
        });
        return;
      }

      // get lists of unique ids for fetching display info
      const userIds = results
        .map(r => r.user_id)
        .filter(Boolean)
        .filter(notADuplicate);
      const groupIds = results
        .map(r => r.groupId)
        .filter(Boolean)
        .filter(notADuplicate);

      // make quads of user_id, groupId, notificationInfo, query
      const quads = results.reduce(
        (
          acc,
          {
            user_id,
            groupId,
            bucketId,
            notificationInfo,
            defaultAssignee: _defaultAssignee,
            query,
            viewOnly
          }
        ) => {
          // empty user, do nothing
          var matches;
          if (!user_id) {
            return acc;
          }

          if (groupId) {
            matches = acc.filter(
              one =>
                one.userId === user_id &&
                one.notificationInfo === notificationInfo &&
                one.defaultAssignee === _defaultAssignee &&
                one.bucketId === bucketId
            );
            if (matches && matches.length) {
              if (matches[0].groups) {
                matches[0].groups.push(groupId);
              } else {
                matches[0].groups = [groupId];
              }
            } else {
              acc.push({
                userId: user_id,
                groups: [groupId],
                notificationInfo: notificationInfo,
                defaultAssignee: _defaultAssignee,
                bucketId: bucketId,
                query,
                viewOnly
              });
            }
          } else if (query) {
            matches = acc.filter(
              one => one.userId === user_id && one.bucketId === bucketId
            );
            if (matches && matches.length) {
              matches[0].query = query;
            } else {
              acc.push({
                userId: user_id,
                notificationInfo: notificationInfo,
                defaultAssignee: _defaultAssignee,
                bucketId: bucketId,
                query,
                viewOnly
              });
            }
          } else {
            acc.push({
              userId: user_id,
              notificationInfo: notificationInfo,
              defaultAssignee: _defaultAssignee,
              bucketId: bucketId,
              query,
              viewOnly
            });
          }
          return acc;
        },
        []
      );

      // query to fetch groups diaplay info
      const groupsQuery =
        groupIds.length > 0
          ? groupIds

              .map(
                id =>
                  `{"field": "'id'", "op": "=", "value": "${id}", "internOp": "OR"}`
              )
              .join(',')
          : null;

      // fetch in parallel and pass results to next promise
      return Promise.all([
        userIds,
        quads,
        fetchAll(
          `/users/v1/users?query=[[{"field":"\'rolesData\'->\'role\'","op":"=","value":"admin"}]]`
        ).then(({ results: r }) => r),
        groupsQuery
          ? fetchAPI(`/users/v1/usergroup?query=[[${groupsQuery}]]`).then(
              ({ results: r }) => r
            )
          : []
      ]).then(([ids, map, usersData, groupsData]) => {
        const state = map
          .map((quad, i) => {
            let userInfo = usersData.find(u => u.id === quad.userId);
            if (quad.userId) {
              if (userInfo) {
                return {
                  ...getUserLabel(userInfo),
                  groups: quad.groups,
                  query: quad.query,
                  notificationInfo: quad.notificationInfo,
                  defaultAssignee: quad.defaultAssignee,
                  bucketId: quad.bucketId,
                  viewOnly: quad.viewOnly
                };
              } else {
                return null;
              }
            } else {
              userInfo = usersData.find(u => u.id === quad.userId);
              if (userInfo) {
                return getUserLabel(userInfo);
              } else {
                return null;
              }
            }
          })
          .filter(Boolean)
          .sort((a, b) => {
            let aName, bName;
            if (a && a.label) {
              aName = a.label.toLowerCase();
            }
            if (b && b.label) {
              bName = b.label.toLowerCase();
            }
            if (aName < bName) return -1;
            if (aName > bName) return 1;
            return 0;
          });

        dispatch({
          type: 'load state',
          state
        });

        const foundDefaultCollaborators = results.filter(
          c => c.defaultAssignee
        );

        if (
          foundDefaultCollaborators.length &&
          foundDefaultCollaborators[0].user_id !== defaultAssignee
        ) {
          setDefaultAssignee(foundDefaultCollaborators[0].user_id);
        }
      });
    });
  };

  useEffect(() => {
    loadCollaborators();
  }, [fetchAPI, formId]);

  useEffect(() => {
    if (triggerSaveWhenDispatchDone || changedDefaultAssignee) {
      clickedSaveCollaborators(true);
      setTriggerSaveWhenDispatchDone(false);
      setChangedDefaultAssignee(false);
    }
  }, [triggerSaveWhenDispatchDone, changedDefaultAssignee]);

  const closeWizard = useCallback(() => {
    setActiveView(prevActiveView.current);
    reloadForm().then(() => {
      loadCollaborators();
    });
  }, [reloadForm, setActiveView, loadCollaborators]);

  const interpretCollaboratorsAndSave = useCallback(
    listOfDistinctCollaborators => {
      let convertedListForAPI;
      convertedListForAPI = [];
      listOfDistinctCollaborators.forEach(oneDistinctCollaborator => {
        let oneCopy, oneQuery;
        const { user_id, groups, notificationInfo, value } =
          oneDistinctCollaborator;
        oneQuery = oneDistinctCollaborator.query;
        if (!oneQuery || !oneQuery.length || oneQuery === '[]') {
          oneQuery = '';
        }
        const actualUserId = user_id || value;
        const body = {
          assetId: formId,
          user_id: actualUserId,
          type: 'form_admin',
          notificationInfo,
          bucketId: oneDistinctCollaborator.bucketId
        };
        if (oneDistinctCollaborator.viewOnly) {
          body.viewOnly = true;
        }
        if (
          defaultAssignee === actualUserId &&
          !oneQuery &&
          !oneDistinctCollaborator.viewOnly
        ) {
          body.defaultAssignee = true;
        }
        if (groups && groups.length) {
          // push one item per group
          groups.forEach(oneGroup => {
            oneCopy = { ...body };
            if (oneGroup !== 'all') oneCopy.groupId = oneGroup;
            if (oneCopy.groupId && oneCopy.defaultAssignee) {
              delete oneCopy.defaultAssignee;
            }
            convertedListForAPI.push(oneCopy);
          });
          if (oneQuery && oneQuery.length) {
            // if there's also a query push one separate from groups ones
            oneCopy = { ...body, query: oneQuery };
            convertedListForAPI.push(oneCopy);
          }
        } else {
          // just one item
          oneCopy = { ...body };
          if (oneQuery && oneQuery.length) {
            oneCopy.query = oneQuery;
          }
          convertedListForAPI.push(oneCopy);
        }
      });
      fetchAPI(`/forms/v1/forms/${formId}/collaborators`, {
        method: 'POST',
        body: { filters: convertedListForAPI }
      })
        .then(() => {
          openSnackbar('success', 'snackbars.assignees.success');
        })
        .catch(e => {
          openSnackbar('error', 'snackbars.assignees.failure');
        })
        .finally(() => {
          loadCollaborators();
          if (activeView === ACTIVE_VIEW.COLLABORATOR_WIZARD) {
            closeWizard();
          }
          setSubmitting(false);
        });
    },
    [
      activeView,
      defaultAssignee,
      fetchAPI,
      formId,
      loadCollaborators,
      closeWizard,
      openSnackbar
    ]
  );

  const clickedSaveCollaborators = (confirmed = false) => {
    setSubmitting(true);
    const localSets = collaborators
      .flatMap(col => {
        return {
          user_id: col.value,
          groups: col.groups,
          notificationInfo: col.notificationInfo,
          alreadyAdmin: col.alreadyAdmin,
          bucketId: col.bucketId
        };
      })
      .filter(set => set.user_id);

    if (!confirmed && localSets.filter(one => !one.alreadyAdmin).length) {
      setAskingToConfirm(true);
      return;
    }

    interpretCollaboratorsAndSave(collaborators);
  };

  const filterCollaborators = (c, f) => {
    let lowerCaseFilter, lowerFirstName, lowerLastName, fullName, lowerEmail;
    lowerCaseFilter = f ? f.toLowerCase() : '';
    lowerFirstName = c && c.firstName ? c.firstName.toLowerCase() : '';
    lowerLastName = c && c.lastName ? c.lastName.toLowerCase() : '';
    fullName = lowerFirstName + ' ' + lowerLastName;
    lowerEmail = c && c.email ? c.email.toLowerCase() : '';
    lowerCaseFilter = lowerCaseFilter.trim();
    fullName = fullName.trim();
    lowerEmail = lowerEmail.trim();
    if (
      !f ||
      fullName.indexOf(lowerCaseFilter) !== -1 ||
      lowerEmail.indexOf(lowerCaseFilter) !== -1
    ) {
      return true;
    }

    return false;
  };

  const filterSectionAdmins = (groupName, filter) => {
    const lowerCaseFilter = filter ? filter.toLowerCase().trim() : '';
    const lowerGroupName = groupName ? groupName.toLowerCase() : '';

    return !filter || lowerGroupName.includes(lowerCaseFilter);
  };

  useEffect(() => {
    //init
    form.buckets.forEach(bucket => {
      bucketNames.current[bucket.internalId] = bucket.name;
    });
  }, []);

  const getAdminGroupName = bucketId => bucketNames.current[bucketId];

  const calculatedReducerState = useMemo(() => {
    const formAdmins = [];
    const viewOnlyAdmins = [];
    const sectionAdmins = [];

    collaborators.forEach(c => {
      if (!c.bucketId) {
        if (filterCollaborators(c, filter)) {
          if (c.viewOnly) {
            viewOnlyAdmins.push(c);
          } else {
            formAdmins.push(c);
          }
        }
      } else {
        const name = getAdminGroupName(c.bucketId);
        if (filterSectionAdmins(name, filter)) {
          sectionAdmins.push(c);
        }
      }
    });

    const bucketMap = {};
    sectionAdmins.forEach(admin => {
      bucketMap[admin.bucketId] = [...(bucketMap[admin.bucketId] || []), admin];
    });

    const groupedSectionAdmins = [];
    Object.keys(bucketMap).forEach(bucketId => {
      let value = bucketMap[bucketId].at(0);
      value = {
        bucketId: value.bucketId,
        groups: value.groups,
        query: value.query,
        groupSize: bucketMap[bucketId].length
      };
      groupedSectionAdmins.push(value);
    });

    return {
      bucketMap,
      formAdmins,
      viewOnlyAdmins,
      sectionAdmins: groupedSectionAdmins
    };
  }, [collaborators, filter]);

  const removeAdminGroupFromReducer = group => {
    dispatch({
      type: 'delete multi admin',
      userIds: group
    });
  };

  const deleteAdminGroup = () => {
    const adminGroup = warningDialogOpen.which;
    const adminResults = [];

    serverSets.current.forEach(element => {
      if (element.bucketId !== adminGroup.bucketId) {
        const obj = {
          ...element,
          assetId: element.assetId,
          user_id: element.user_id,
          type: element.type,
          notificationInfo: element.notificationInfo,
          bucketId: element && element.bucketId ? element.bucketId : undefined,
          groupId: element && element.groups ? element.groups : undefined
        };
        adminResults.push(obj);
      }
    });

    setIsLoading(true);
    fetchAPI(
      `/forms/v1/forms/${form.id}/collaborate/bucket/${adminGroup.bucketId}`,
      {
        method: 'DELETE',
        body: {
          filters: adminResults
        }
      }
    )
      .then(() => {
        removeAdminGroupFromReducer(
          calculatedReducerState.bucketMap[adminGroup.bucketId]
        );
        loadCollaborators();
      })
      .catch(e => {
        openSnackbar('error', 'snackbars.assignees.failure');
      })
      .finally(() => {
        setIsLoading(false);
        setWarningDialogOpen(false);
      });
  };

  const removeAdminFromReducer = adminId => {
    var filtered, user_id;
    user_id = '';
    if (warningDialogOpen.action === 'delete') {
      filtered = collaborators.filter(c => c._id === adminId);
      if (filtered && filtered.length) {
        user_id = filtered[0].value;
      }
      dispatch({
        type: 'delete admin',
        _id: adminId
      });
      if (user_id === defaultAssignee) {
        setChangedDefaultAssignee(true);
        setDefaultAssignee(null);
      }
      setTriggerSaveWhenDispatchDone(true);
      setWarningDialogOpen(false);
    } else if (warningDialogOpen.action === 'group') {
      dispatch({
        type: 'set groups',
        _id: adminId,
        groups: warningDialogOpen.groups
      });
    }
    setChangedDefaultAssignee(true);
    setDefaultAssignee(null);
    setWarningDialogOpen(false);
  };

  const deleteAdmin = () => {
    if (warningDialogOpen.type === 'section_admin') {
      deleteAdminGroup();
    } else {
      const adminId = warningDialogOpen.which;
      removeAdminFromReducer(adminId);
    }
  };

  const onEdit = a => {
    openFormAdminWizard(a);
  };

  const onAddNewAdmin = () => {
    openFormAdminWizard({});
  };

  const onAddNewAdminGroup = () => {
    openAdminGroupWizard({});
  };

  const onAdminGroupEdit = a => {
    const groupName = getAdminGroupName(a.bucketId);
    const users = calculatedReducerState.bucketMap[a.bucketId];
    const fieldIds = bucketFields.current[a.bucketId] || [];
    const userObjects = users.map(user => ({
      label: `${user.label} <${user.email}>`,
      value: user.value
    }));

    if (!fieldIds.length) {
      form.adminFields.forEach(field => {
        if (field.restrictions.find(rest => rest.id === a.bucketId)) {
          fieldIds.push(field.internalId);
        }
      });

      form.fields.forEach(field => {
        if (field.restrictions.find(rest => rest.id === a.bucketId)) {
          fieldIds.push(field.internalId);
        }
      });
    }

    const formatedObj = {
      id: a.bucketId,
      name: groupName,
      userIds: userObjects,
      permissions: {
        groupIds: a.groups,
        fieldIds: fieldIds,
        query: a.query
      },
      queryFilters: {
        filters: []
      }
    };
    openAdminGroupWizard(formatedObj);
  };

  const onSearch = useCallback(searchValue => {
    setFilter(!!searchValue ? searchValue : null);
  }, []);

  const clickedDelete = id => {
    const countOnPageLoad =
      serverSets && serverSets.current ? serverSets.current.length : 0;
    if (countOnPageLoad || hasSubmissions) {
      setWarningDialogOpen({
        action: 'delete',
        which: id,
        type: 'form_admin'
      });
    } else {
      dispatch({ type: 'delete admin', _id: id });
    }
  };

  const clickedDeleteOnAdminGroup = adminGroup => {
    setWarningDialogOpen({
      action: 'delete',
      which: adminGroup,
      type: 'section_admin'
    });
  };

  const viewProps = {
    allGroups,
    askingToConfirm,
    clickedDelete,
    clickedDeleteOnAdminGroup,
    clickedSaveCollaborators,
    collaborators: calculatedReducerState.formAdmins || [],
    sectionAdmins: calculatedReducerState.sectionAdmins || [],
    viewOnlyCollaborators: calculatedReducerState.viewOnlyAdmins || [],
    collaboratorsWithoutAGroup,
    defaultAssignee,
    deleteAdmin,
    dispatch,
    eligibleForDefaultAdmin,
    form,
    hasSubmissions,
    onAddNewAdmin,
    onAddNewAdminGroup,
    onAdminGroupEdit,
    onEdit,
    getAdminGroupName,
    onSearch,
    serverSets,
    setAskingToConfirm,
    setChangedDefaultAssignee,
    setDefaultAssignee,
    setSubmitting,
    setWarningDialogOpen,
    submitting,
    warningDialogOpen
  };

  const wizardProps = {
    allGroups,
    defaultAssignee,
    collaborators,
    editMode,
    form,
    formId,
    interpretCollaboratorsAndSave,
    loadCollaborators,
    onWizardClose: closeWizard,
    collaboratorData: collaboratorInEdit || {},
    setWarningDialogOpen
  };

  const contextProps = {
    form
  };

  return (
    <CollaboratorsContext.Provider value={contextProps}>
      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.COLLABORATOR_LIST}
      >
        <FormBuilderCollaborateView {...viewProps} />
      </Animation>
      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.COLLABORATOR_WIZARD}
        unmountOnExit
        mountOnEnter
      >
        <FormBuilderCollaborateWizardEngine {...wizardProps} />
      </Animation>
      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.ADMIN_GROUP_WIZARD}
        unmountOnExit
        mountOnEnter
      >
        <AdminGroupWizardEngine
          groupOptions={allGroups}
          adminGroupData={collaboratorInEdit}
          bucketNames={bucketNames}
          bucketFields={bucketFields}
          collaborators={serverSets.current}
          closeWizard={closeWizard}
        />
      </Animation>
      <GeneralDialog
        open={warningDialogOpen}
        handleClose={() => {
          setWarningDialogOpen(false);
        }}
        handleConfirm={deleteAdmin}
        title="warnings.deleting_assignee.title"
        content="warnings.deleting_assignee"
      />
    </CollaboratorsContext.Provider>
  );
};

export default FormBuilderCollaborateEngine;
